import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import CaipirinhaRequest from '../../requests/caipirinha-api/caipirinha.request';
import InternalServerErrorException from '../../requests/caipirinha-api/exceptions/internal-server-error.exception';
import LocalStorageService from '../../services/local-storage.service';
import PaymentNotAuthorizedException from '../../requests/caipirinha-api/exceptions/payment-not-authorized.exception';
import OrderPaidException from '../../requests/caipirinha-api/exceptions/order-paid.exception';
import ProductOutOfStockException from '../../requests/caipirinha-api/exceptions/product-out-of-stock.exception';
import BoletoExpiredException from '../../requests/caipirinha-api/exceptions/boleto-expired.exception';
import AlertConstants from '../../constants/alert.constant';
import { useAlert } from '../../containers/Alert';
import { Order } from '../../requests/caipirinha-api/interfaces/order.interface';
import {
  getPaymentName,
  getPaymentPage,
  isBoleto,
  isPix,
} from '../../utils/paymentType.utils';
import { setActionToGA } from '../../utils/react-ga.utils';
import PixExpiredException from '../../requests/caipirinha-api/exceptions/pix-expired.exception';
import { getTimerAction, replaceTime } from '../../utils/time.utils';
import PixRefusedException from '../../requests/caipirinha-api/exceptions/pix-refused.exception';
import parseOrder from '../../requests/caipirinha-api/parse/order.parse';
import parsePutCheckoutToOrder from '../../requests/caipirinha-api/parse/order-put-checkout-to-order';
import { LGPDInvite } from '../../requests/interfaces/commons.interface';
import { getTMXSessionId } from '../../utils/threatmetrix';
import SessionIdNotAcceptableException from '../../requests/caipirinha-api/exceptions/session-id-not-acceptable-exception';
import { LegalNatureType, legalNature } from '../../constants/legalNature.enum';
import SaleToCnpjNotAllowed from '../../requests/caipirinha-api/exceptions/sale-to-cnpj-not-allowed.exception';
import OrderCannotBeCanceled from '../../requests/caipirinha-api/exceptions/order-cannot-be-canceled-exception';
import SellerDoesNotSellToCnpj from '../../requests/caipirinha-api/exceptions/seller-does-not-sell-to-cnpj.exception';

export const useOrder = () => {
  const { push, replace } = useHistory();
  const { alert, close, setTitle } = useAlert();
  const [loading, setLoading] = useState<boolean>(false);
  const [nameLoading, setNameLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [order, setOrder] = useState<Order | undefined>();
  const [lgpd, setLgpd] = useState<LGPDInvite | undefined>();
  const [customer, setCustomer] = useState<{
    name: string;
    legalNature: LegalNatureType;
  } | null>(null);
  const [session, setSession] = useState<string | null>(null);

  const confirmPayment = async (
    id: string,
    encrypted: string,
    fingerprint?: string,
  ) => {
    try {
      setConfirmLoading(true);

      const sessionId = getTMXSessionId();

      await CaipirinhaRequest.confirmPayment(
        id,
        encrypted,
        fingerprint,
        sessionId,
      );
      setActionToGA(1);

      replace(`/${id}/pagamento-realizado-com-sucesso`, { order, lgpd });
    } catch (error) {
      if (error instanceof PaymentNotAuthorizedException) {
        replace(`/${id}/erro-no-pagamento`);
      } else if (error instanceof SessionIdNotAcceptableException) {
        replace(`/${id}/erro-no-pagamento`, { code: 4024 });
      } else if (error instanceof ProductOutOfStockException) {
        replace(`/${id}/produto-sem-estoque`, error.message);
      } else if (error instanceof SellerDoesNotSellToCnpj) {
        replace(`/${id}/seller-nao-aceita-venda-para-cnpj`);
      } else {
        alert(AlertConstants.error);
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  const generatePaymentForm = async (
    id: string,
    document: string,
    paymentId: string,
    fingerprint?: string,
  ) => {
    try {
      setConfirmLoading(true);

      const sessionId = getTMXSessionId();
      const response = await CaipirinhaRequest.confirmPayment(
        id,
        undefined,
        fingerprint,
        sessionId,
      );
      setActionToGA(1);

      const page = getPaymentPage(paymentId);

      if (isPix(paymentId) || isBoleto(paymentId)) {
        const parse = parsePutCheckoutToOrder(response, id);
        replace(`/${id}/${document}/${page}`, { resultPayment: parse });
      } else replace(`/${id}/${document}/${page}`);
    } catch (error) {
      if (error instanceof PixRefusedException)
        alert(AlertConstants.errorRefusedPix);
      else if (error instanceof ProductOutOfStockException)
        alert(AlertConstants.errorProductOutOfStock);
      else if (error instanceof SellerDoesNotSellToCnpj) alert(AlertConstants.sellerDoesNotSellToCnpj);
      else if (isBoleto(paymentId)) alert(AlertConstants.errorGenerateBoleto);
      else if (isPix(paymentId)) alert(AlertConstants.errorGeneratePix);
      else alert(AlertConstants.error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const deleteOrder = async (id: string) => {
    try {
      await CaipirinhaRequest.deleteOrder(id);
      return true;
    } catch (error) {
      if (error instanceof OrderCannotBeCanceled) {
        alert(AlertConstants.orderCannotBeCanceled);
      }

      if (error instanceof InternalServerErrorException) {
        alert(AlertConstants.error);
      }

      return false;
    }
  };

  const getNameById = async (id: string) => {
    setNameLoading(true);
    const response = await CaipirinhaRequest.getNameAndExpirationTimeById(id);

    setCustomer(
      response == null
        ? null
        : {
            name: response.name,
            legalNature: response.legalNature,
          },
    );

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    response !== null &&
      response.session !== undefined &&
      response.session.id !== null &&
      setSession(response.session.id);

    setNameLoading(false);

    // Show order expiration time
    if (response?.expiration &&
      (isBoleto(response.paymentId) || isPix(response.paymentId))
    ) {
      const { hours, minutes, seconds, totalMinutes } = response.expiration;

      if (!(hours === 0 && minutes === 0 && seconds === 0)) {
        const paymentName = getPaymentName(response.paymentId);

        const title = `Faltam %s para o link de geração do ${paymentName} expirar!`;

        alert(
          AlertConstants.orderExpireTime(
            replaceTime(title, {
              hours,
              minutes,
              seconds,
            }),
            getTimerAction(title, { hours, minutes, seconds }, close, setTitle),
            response.paymentId,
            totalMinutes,
          ),
        );
      }
    }
  };

  const getOrderPaidByIdAndDocument = async (id: string, document: string) => {
    try {
      setLoading(true);
      const response = await CaipirinhaRequest.getOrderPaidByIdAndDocument(
        id,
        document,
        customer ? customer.legalNature : legalNature.FISICAL,
      );
      setOrder(response.order);
      return response;
    } catch (error) {
      if (error instanceof InternalServerErrorException) {
        alert(AlertConstants.error);
      } else {
        alert(AlertConstants.notFoundReceipt);
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getOrderByIdAndDocument = async (id: string, document: string) => {
    try {
      setLoading(true);
      const response = await CaipirinhaRequest.getOrderByIdAndDocument(
        id,
        document,
        customer ? customer.legalNature : legalNature.FISICAL,
      );
      const parse = parseOrder(response.order);
      setOrder(parse);
      LocalStorageService.setItem(id, parse);

      const showMessageToken = !!response.showMessageToken;
      LocalStorageService.setItem('showMessageToken', showMessageToken);

      if (response.lgpd) {
        setLgpd(response.lgpd);
      }
    } catch (error) {
      if (error instanceof InternalServerErrorException) {
        alert(AlertConstants.error);
      } else if (error instanceof OrderPaidException) {
        const result = await getOrderPaidByIdAndDocument(id, document);
        replace(`/${id}/comprovante`, {
          order: result?.order,
          lgpd: result?.lgpd,
        });
      } else if (error instanceof BoletoExpiredException) {
        replace(`/${id}/boleto-expirado`);
      } else if (error instanceof PixExpiredException) {
        replace(`/${id}/pix-expirado`);
      } else if (error instanceof ProductOutOfStockException) {
        replace(`/${id}/produto-sem-estoque`);
      } else if (error instanceof SaleToCnpjNotAllowed) {
        replace(`/${id}/venda-nao-permitida`);
      } else {
        push(`/${id}/pedido-nao-encontrado`);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    nameLoading,
    confirmLoading,
    order,
    lgpd,
    customer,
    session,
    confirmPayment,
    generatePaymentForm,
    deleteOrder,
    getNameById,
    getOrderByIdAndDocument,
    getOrderPaidByIdAndDocument,
  };
};
